import React, { useState, useEffect } from "react";
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Sales Report Display Component
const SalesReportDisplay = ({ formData, filters, branchProfile, salesData }) => {
  // Helper function to parse date
  const parseSaleDate = (dateString) => {
    if (!dateString) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split('-');
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    if (/^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-\/]/);
      return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    }
    return new Date(dateString);
  };

  // Format date to DD/MM/YYYY for display
  const formatDisplayDate = (dateString) => {
    const date = parseSaleDate(dateString);
    return !date || isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Safe string comparison helper
  const safeStringCompare = (a, b) => {
    const strA = String(a || '');
    const strB = String(b || '');
    return strA.localeCompare(strB);
  };

  let filteredSalesData = salesData;

  // Apply date filter for ALL reports (not just datewise)
  const fromDate = new Date(formData.fromDate);
  const toDate = new Date(formData.toDate);
  toDate.setHours(23, 59, 59, 999); // Include the entire end date

  filteredSalesData = filteredSalesData.filter(item => {
    const itemDate = parseSaleDate(item.date);
    return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate;
  });

  // Apply store filter
  if (!filters.store.includes("All Stores")) {
    filteredSalesData = filteredSalesData.filter(item =>
      filters.store.includes(item.store)
    );
  }

  // Apply customer filter
  if (!filters.customer.includes("All Customers")) {
    filteredSalesData = filteredSalesData.filter(item =>
      filters.customer.includes(item.customer)
    );
  }

  // Apply group filter
  if (!filters.group.includes("All Group Names")) {
    filteredSalesData = filteredSalesData.filter(item =>
      filters.group.includes(item.groupName)
    );
  }

  // Apply category filter
  if (!filters.category.includes("All Categories")) {
    filteredSalesData = filteredSalesData.filter(item =>
      filters.category.includes(item.category)
    );
  }

  // Apply brand filter
  if (!filters.brand.includes("All Brands")) {
    filteredSalesData = filteredSalesData.filter(item =>
      filters.brand.includes(item.brand)
    );
  }

  // Apply product filter
  if (!filters.product.includes("All Products")) {
    filteredSalesData = filteredSalesData.filter(item =>
      filters.product.includes(item.itemDescription)
    );
  }

  // Apply salesman filter
  if (!filters.salesman.includes("All Salesmen")) {
    filteredSalesData = filteredSalesData.filter(item =>
      filters.salesman.includes(item.salesMan)
    );
  }

  // Initialize displayData
  let displayData = [];

  // Process data based on search criteria and report type
  if (formData.reportType === "summary") {
    if (formData.searchBy === "termwise") {
      // Termwise Abstract Report - Group by date and sum payment modes
      const groupedData = filteredSalesData.reduce((acc, item) => {
        const key = item.date;
        if (!acc[key]) {
          acc[key] = {
            date: key,
            cash: 0,
            card: 0,
            credit: 0,
            total: 0
          };
        }

        const netAmount = parseFloat(item.netAmount) || 0;
        const paymentMode = (item.paymentMode || 'cash').toLowerCase();

        if (paymentMode === 'cash') {
          acc[key].cash += netAmount;
        } else if (paymentMode === 'card') {
          acc[key].card += netAmount;
        } else if (paymentMode === 'credit') {
          acc[key].credit += netAmount;
        }

        acc[key].total += netAmount;
        return acc;
      }, {});

      displayData = Object.values(groupedData);
      displayData.sort((a, b) => parseSaleDate(a.date) - parseSaleDate(b.date));
    } else {
      // Regular summary reports
      const groupedData = filteredSalesData.reduce((acc, item) => {
        let key;
        switch (formData.searchBy) {
          case "datewise":
            key = item.date;
            break;
          case "store":
            key = item.store;
            break;
          case "customer":
            key = item.customer;
            break;
          case "groupwise":
            key = item.groupName;
            break;
          case "category":
            key = item.category;
            break;
          case "brand":
            key = item.brand;
            break;
          case "productwise":
            key = item.itemDescription;
            break;
          case "salesman":
            key = item.salesMan;
            break;
          case "counterwise":
            key = item.counter || 'Unknown Counter';
            break;
          default:
            key = item.date;
        }

        if (!acc[key]) {
          acc[key] = {
            key: key,
            billNos: [],
            quantity: 0,
            grossValue: 0,
            discount: 0,
            gstValue: 0,
            cessValue: 0,
            netValue: 0,
            count: 0
          };
        }

        acc[key].billNos.push(item.billNo);
        acc[key].quantity += parseFloat(item.qty) || 0;

        // Calculate gross value
        const qty = parseFloat(item.qty) || 0;
        const unitRate = parseFloat(item.unitRate) || 0;
        const gross = qty * unitRate;
        const discount = parseFloat(item.discount) || 0;
        const taxAmount = parseFloat(item.taxAmount) || 0;
        const cessAmount = parseFloat(item.cessAmount) || 0;
        const netValueItem = gross - discount + taxAmount + cessAmount;

        acc[key].grossValue += gross;
        acc[key].discount += discount;

        // GST value should be just the taxAmount (not including CESS)
        acc[key].gstValue += taxAmount;
        acc[key].cessValue += cessAmount;

        acc[key].netValue += netValueItem;
        acc[key].count += 1;
        return acc;
      }, {});

      displayData = Object.values(groupedData);
      displayData.sort((a, b) => {
        if (formData.searchBy === "datewise") {
          return parseSaleDate(a.key) - parseSaleDate(b.key);
        }
        return safeStringCompare(a.key, b.key);
      });
    }
  } else {
    // For detailed reports
    if (formData.searchBy === "termwise") {
      // Termwise Details Report - Show individual invoices with payment breakdown
      displayData = [...filteredSalesData];
    } else {
      displayData = [...filteredSalesData];
    }

    // Sort detailed data
    displayData.sort((a, b) => {
      if (formData.searchBy === "datewise") {
        return parseSaleDate(a.date) - parseSaleDate(b.date);
      } else if (formData.searchBy === "store") {
        return safeStringCompare(a.store, b.store) || parseSaleDate(a.date) - parseSaleDate(b.date);
      } else if (formData.searchBy === "customer") {
        return safeStringCompare(a.customer, b.customer) || parseSaleDate(a.date) - parseSaleDate(b.date);
      } else if (formData.searchBy === "groupwise") {
        return safeStringCompare(a.groupName, b.groupName) || parseSaleDate(a.date) - parseSaleDate(b.date);
      } else if (formData.searchBy === "category") {
        return safeStringCompare(a.category, b.category) || parseSaleDate(a.date) - parseSaleDate(b.date);
      } else if (formData.searchBy === "brand") {
        return safeStringCompare(a.brand, b.brand) || parseSaleDate(a.date) - parseSaleDate(b.date);
      } else if (formData.searchBy === "productwise") {
        return safeStringCompare(a.itemDescription, b.itemDescription) || parseSaleDate(a.date) - parseSaleDate(b.date);
      } else if (formData.searchBy === "salesman") {
        return safeStringCompare(a.salesMan, b.salesMan) || parseSaleDate(a.date) - parseSaleDate(b.date);
      } else if (formData.searchBy === "counterwise") {
        return safeStringCompare(a.counter || '', b.counter || '') || parseSaleDate(a.date) - parseSaleDate(b.date);
      } else if (formData.searchBy === "invoicewise") {
        return safeStringCompare(a.billNo, b.billNo) || parseSaleDate(a.date) - parseSaleDate(b.date);
      } else if (formData.searchBy === "termwise") {
        return safeStringCompare(a.billNo, b.billNo) || parseSaleDate(a.date) - parseSaleDate(b.date);
      }
      return parseSaleDate(a.date) - parseSaleDate(b.date);
    });
  }

  // Ensure displayData is always an array
  displayData = Array.isArray(displayData) ? displayData : [];

  // Calculate totals based on report type
  let totalQty, totalGrossValue, totalDiscount, totalGstValue, totalCessValue, totalBillDiscount, totalRoundOff, totalNetValue;
  let totalCash, totalCard, totalCredit, totalAmount;

  if (formData.searchBy === "termwise" && formData.reportType === "summary") {
    totalCash = displayData.reduce((sum, item) => sum + (parseFloat(item.cash) || 0), 0);
    totalCard = displayData.reduce((sum, item) => sum + (parseFloat(item.card) || 0), 0);
    totalCredit = displayData.reduce((sum, item) => sum + (parseFloat(item.credit) || 0), 0);
    totalAmount = displayData.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  } else if (formData.searchBy === "termwise" && formData.reportType === "detailed") {
    totalCash = displayData.reduce((sum, item) => {
      const paymentMode = (item.paymentMode || 'cash').toLowerCase();
      return sum + (paymentMode === 'cash' ? (parseFloat(item.netAmount) || 0) : 0);
    }, 0);
    totalCard = displayData.reduce((sum, item) => {
      const paymentMode = (item.paymentMode || 'cash').toLowerCase();
      return sum + (paymentMode === 'card' ? (parseFloat(item.netAmount) || 0) : 0);
    }, 0);
    totalCredit = displayData.reduce((sum, item) => {
      const paymentMode = (item.paymentMode || 'cash').toLowerCase();
      return sum + (paymentMode === 'credit' ? (parseFloat(item.netAmount) || 0) : 0);
    }, 0);
    totalAmount = displayData.reduce((sum, item) => sum + (parseFloat(item.netAmount) || 0), 0);
  } else {
    totalQty = displayData.reduce((sum, item) => sum + (parseFloat(item.quantity) || parseFloat(item.qty) || 0), 0);
    totalGrossValue = displayData.reduce((sum, item) => {
      if (formData.reportType === "summary") {
        return sum + (parseFloat(item.grossValue) || 0);
      } else {
        const qty = parseFloat(item.qty) || 0;
        const unitRate = parseFloat(item.unitRate) || 0;
        return sum + (qty * unitRate);
      }
    }, 0);
    totalDiscount = displayData.reduce((sum, item) => sum + (parseFloat(item.discount) || 0), 0);

    // GST value should be just the taxAmount (not including CESS)
    totalGstValue = displayData.reduce((sum, item) => sum + (parseFloat(item.gstValue) || parseFloat(item.taxAmount) || 0), 0);
    totalCessValue = displayData.reduce((sum, item) => sum + (parseFloat(item.cessValue) || parseFloat(item.cessAmount) || 0), 0);
    totalBillDiscount = displayData.reduce((sum, item) => sum + (parseFloat(item.billDiscount) || 0), 0);
    totalRoundOff = displayData.reduce((sum, item) => sum + (parseFloat(item.roundOff) || 0), 0);

    // FIXED: Calculate net value properly for all report types
    if (formData.reportType === "summary") {
      // For summary reports, use the calculated netValue from grouped data
      totalNetValue = displayData.reduce((sum, item) => sum + (parseFloat(item.netValue) || 0), 0);
    } else {
      // For detailed reports, use netAmount from individual records
      totalNetValue = displayData.reduce((sum, item) => sum + (parseFloat(item.netAmount) || 0), 0);
    }
  }

  const getReportTitle = () => {
    switch (formData.searchBy) {
      case "datewise": return "Datewise Sales Report";
      case "store": return "Store-wise Sales Report";
      case "customer": return "Customer-wise Sales Report";
      case "groupwise": return "Groupwise Sales Report";
      case "category": return "Category-wise Sales Report";
      case "brand": return "Brandwise Sales Report";
      case "productwise": return "Product-wise Sales Report";
      case "salesman": return "Salesman-wise Sales Report";
      case "termwise": return "Term wise Sales Report";
      case "counterwise": return "Counterwise Sales Report";
      case "invoicewise": return "Invoice wise Sales Report";
      default: return "Datewise Sales Report";
    }
  };

const getColumnHeader = () => {
  switch (formData.searchBy) {
    case "datewise": return "Date";
    case "store": return "Store";
    case "customer": return "Customer Name";
    case "groupwise": return "Group Name";
    case "termwise": return "Terms";
    case "counterwise": return "Counter";
    case "invoicewise": return "Invoice";
    case "productwise": return "Product Name";
    case "brand": return "Brand Name";
    case "category": return "Category Name";
    case "salesman": return "Salesman Name"; // ← FIX: Added salesman case
    default: return "Date";
  }
};

  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  const renderTableHeaders = () => {
    if (formData.searchBy === "termwise" && formData.reportType === "summary") {
      // Termwise Abstract Report Headers
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Date</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Cash</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Card</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Credit</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Total</th>
        </tr>
      );
    } else if (formData.searchBy === "termwise" && formData.reportType === "detailed") {
      // Termwise Details Report Headers
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>INV.NO.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>INV.DT.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Customer Name</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Cash. Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Card. Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Credit. Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if (formData.searchBy === "invoicewise" && formData.reportType === "detailed") {
      // Invoicewise Details Report Headers
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Inv No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Date</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>HSN</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Salesman</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Customer</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Phone</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Payment</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Product</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Rate</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Tax%</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Gross</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Disc</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>CGST</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>SGST</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>IGST</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>GST</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>CESS</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontWeight: '600', fontSize: '10px' }}>Net</th>
        </tr>
      );
    } else if (formData.reportType === "summary") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            {getColumnHeader()}
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Quantity</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Value</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CESS Value</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amount</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if (formData.searchBy === "brand" && formData.reportType === "detailed") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Brand</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Bill No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Date</th>
          {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Store</th> */}
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Customer</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Unit</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Unit Rate</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CESS Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Bill Disc.</th>
          {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>RoundOff</th> */}
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amount</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if (formData.searchBy === "productwise" && formData.reportType === "detailed") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Unit</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Unit Rate</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amount</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Bill No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Date</th>
          {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Store</th> */}
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Customer</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Unit</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Unit Rate</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CESS Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Bill Discount</th>
          {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>RoundOff</th> */}
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amount</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    }
  };

  const renderTableRows = () => {
    if (displayData.length === 0) {
      const colSpan = formData.searchBy === "termwise" && formData.reportType === "summary" ? 6 :
                      formData.searchBy === "termwise" && formData.reportType === "detailed" ? 8 :
                      formData.searchBy === "invoicewise" && formData.reportType === "detailed" ? 20 :
                      formData.reportType === "summary" ? 8 :
                      formData.searchBy === "brand" && formData.reportType === "detailed" ? 17 :
                      formData.searchBy === "productwise" && formData.reportType === "detailed" ? 9 : 16;

      return (
        <tr>
          <td colSpan={colSpan} style={{ 
            border: '1px solid #cbd5e1', 
            padding: '30px', 
            textAlign: 'center', 
            color: '#64748b', 
            fontStyle: 'italic',
            fontSize: '14px',
            backgroundColor: '#f8fafc'
          }}>
            No sales data found for the selected criteria
          </td>
        </tr>
      );
    }

    return displayData.map((item, index) => {
      if (formData.searchBy === "termwise" && formData.reportType === "summary") {
        // Termwise Abstract Report Rows
        return (
          <tr key={item.date} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.date)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.cash.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.card.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.credit.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.total.toFixed(2)}</td>
          </tr>
        );
      } else if (formData.searchBy === "termwise" && formData.reportType === "detailed") {
        // Termwise Details Report Rows
        const paymentMode = (item.paymentMode || 'cash').toLowerCase();
        const netAmount = parseFloat(item.netAmount) || 0;
        const cashAmt = paymentMode === 'cash' ? netAmount : 0;
        const cardAmt = paymentMode === 'card' ? netAmount : 0;
        const creditAmt = paymentMode === 'credit' ? netAmount : 0;

        return (
          <tr key={`${item.saleId}-${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.billNo || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.date)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.customer}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{cashAmt.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{cardAmt.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{creditAmt.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{netAmount.toFixed(2)}</td>
          </tr>
        );
      } else if (formData.searchBy === "invoicewise" && formData.reportType === "detailed") {
        // Invoicewise Details Report Rows
        const qty = parseFloat(item.qty) || 0;
        const unitRate = parseFloat(item.unitRate) || 0;
        const grossAmount = qty * unitRate;
        const discount = parseFloat(item.discount) || 0;
        const taxAmount = parseFloat(item.taxAmount) || 0;
        const cgstAmount = taxAmount / 2; // Assuming equal split for CGST/SGST
        const sgstAmount = taxAmount / 2;
        const igstAmount = 0; // Assuming local sales
        const cessAmount = parseFloat(item.cessAmount) || 0;
        const netValue = parseFloat(item.netValue) || 0; // Use netValue directly from item

        return (
          <tr key={`${item.saleId}-${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontSize: '9px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontSize: '9px' }}>{item.billNo || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontSize: '9px' }}>{formatDisplayDate(item.date)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontSize: '9px' }}>{item.hsnCode || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontSize: '9px' }}>{item.salesMan}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontSize: '9px' }}>{item.customer}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontSize: '9px' }}>{item.phone || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center', fontSize: '9px' }}>{item.paymentMode || 'Cash'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'left', fontSize: '9px' }}>{item.itemDescription}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{qty.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{unitRate.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{((parseFloat(item.tax) || 0)).toFixed(0)}%</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{grossAmount.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{discount.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{cgstAmount.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{sgstAmount.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{igstAmount.toFixed(2)}</td>





            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{taxAmount.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{cessAmount.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'right', fontSize: '9px' }}>{netValue.toFixed(2)}</td>
          </tr>
        );
      } else if (formData.reportType === "summary") {
        return (
          <tr key={item.key} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.key}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.quantity.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.discount.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.gstValue.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.cessValue.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.grossValue.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.netValue.toFixed(2)}</td>
          </tr>
        );
      } else if (formData.searchBy === "brand" && formData.reportType === "detailed") {
        const gross = (parseFloat(item.qty) || 0) * (parseFloat(item.unitRate) || 0);
        return (
          <tr key={`${item.saleId}-${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.brand}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.billNo || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.date)}</td>
            {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.store}</td> */}
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.customer}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.unit || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.qty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.unitRate) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.discount) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.taxAmount) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.cessAmount) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.billDiscount) || 0).toFixed(2)}</td>
            {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.roundOff) || 0).toFixed(2)}</td> */}
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{gross.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.netAmount) || 0).toFixed(2)}</td>
          </tr>
        );
      } else if (formData.searchBy === "productwise" && formData.reportType === "detailed") {
        const gross = (parseFloat(item.qty) || 0) * (parseFloat(item.unitRate) || 0);
        return (
          <tr key={`${item.saleId}-${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.unit || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.qty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.unitRate) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.discount) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.taxAmount) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{gross.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.netAmount) || 0).toFixed(2)}</td>
          </tr>
        );
      } else {
        const gross = (parseFloat(item.qty) || 0) * (parseFloat(item.unitRate) || 0);
        return (
          <tr key={`${item.saleId}-${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.billNo || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.date)}</td>
            {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.store}</td> */}
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.customer}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.unit || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.qty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.unitRate) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.discount) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.taxAmount) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.cessAmount) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.billDiscount) || 0).toFixed(2)}</td>
            {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.roundOff) || 0).toFixed(2)}</td> */}
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{gross.toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.netAmount) || 0).toFixed(2)}</td>
          </tr>
        );
      }
    });
  };

  const renderTotalRow = () => {
    if (displayData.length === 0) return null;

    if (formData.searchBy === "termwise" && formData.reportType === "summary") {
      // Termwise Abstract Total Row
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="2" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCash.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCard.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCredit.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalAmount.toFixed(2)}</td>
        </tr>
      );
    } else if (formData.searchBy === "termwise" && formData.reportType === "detailed") {
      // Termwise Details Total Row
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="4" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCash.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCard.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCredit.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalAmount.toFixed(2)}</td>
        </tr>
      );
    } else if (formData.searchBy === "invoicewise" && formData.reportType === "detailed") {
      // Invoicewise Details Total Row
      const totalGrossAmount = displayData.reduce((sum, item) => {
        const qty = parseFloat(item.qty) || 0;
        const unitRate = parseFloat(item.unitRate) || 0;
        return sum + (qty * unitRate);
      }, 0);

      const totalDiscount = displayData.reduce((sum, item) => sum + (parseFloat(item.discount) || 0), 0);
      const totalTaxAmount = displayData.reduce((sum, item) => sum + (parseFloat(item.taxAmount) || 0), 0);
      const totalCessAmount = displayData.reduce((sum, item) => sum + (parseFloat(item.cessAmount) || 0), 0);
      const totalNetValue = totalGrossAmount - totalDiscount + totalTaxAmount + totalCessAmount;

      const totalCGST = displayData.reduce((sum, item) => sum + ((parseFloat(item.taxAmount) || 0) / 2), 0);
      const totalSGST = displayData.reduce((sum, item) => sum + ((parseFloat(item.taxAmount) || 0) / 2), 0);

      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="9" style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '10px', fontWeight: '600' }}>
            Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>-</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>-</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>{totalGrossAmount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>{totalDiscount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>{totalCGST.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>{totalSGST.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>0.00</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>{totalGstValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>{totalCessValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '10px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    } else if (formData.reportType === "summary") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="2" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCessValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGrossValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    } else if (formData.searchBy === "brand" && formData.reportType === "detailed") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="8" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>-</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCessValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalBillDiscount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalRoundOff.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGrossValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    } else if (formData.searchBy === "productwise" && formData.reportType === "detailed") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="3" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>-</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGrossValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    } else {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="6" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>-</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCessValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalBillDiscount.toFixed(2)}</td>
          {/* <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalRoundOff.toFixed(2)}</td> */}
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGrossValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="print-only">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            margin: '0', 
            marginBottom: '15px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
            <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
              {getReportTitle()}
            </div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
              <span><strong>From:</strong> {new Date(formData.fromDate).toLocaleDateString('en-GB')}</span>
              <span><strong>To:</strong> {new Date(formData.toDate).toLocaleDateString('en-GB')}</span>
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '15px',
          textAlign: 'center',
          minWidth: '150px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
          <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
            Option: {formData.reportType === 'detailed' ? 'Detail' : 'Abstract'}
          </div>
        </div>
      </div>

      {/* Sales Table */}
      <div style={{ overflowX: 'auto' }}>
      <table style={{ 
  width: '100%', 
  borderCollapse: 'collapse', 
  fontSize: '11px', 
  border: '2px solid #3b82f6',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
}} className={
  formData.searchBy === "termwise" && formData.reportType === "summary" ? "termwise-summary" :
  formData.searchBy === "termwise" && formData.reportType === "detailed" ? "termwise-detailed" :
  formData.searchBy === "invoicewise" && formData.reportType === "detailed" ? "invoicewise-detailed" :
  formData.reportType === "summary" ? "summary" :
  formData.searchBy === "brand" && formData.reportType === "detailed" ? "brand-detailed" :
  formData.searchBy === "productwise" && formData.reportType === "detailed" ? "productwise-detailed" :
  "default-detailed"
}>
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {displayData.length > 0 && (
        <div style={{ 
          marginTop: '30px', 
          padding: '20px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{displayData.length}</div>
            </div>
            {formData.searchBy === "termwise" ? (
              <>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Cash Amount</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>₹{(totalCash || 0).toFixed(2)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Card Amount</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>₹{(totalCard || 0).toFixed(2)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Credit Amount</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#f59e0b' }}>₹{(totalCredit || 0).toFixed(2)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Total Amount</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>₹{(totalAmount || 0).toFixed(2)}</div>
                </div>
              </>
            ) : (
              <>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Total Quantity</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{(totalQty || 0).toFixed(2)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Total Gross Amount</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>₹{(totalGrossValue || 0).toFixed(2)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Total Discount</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>₹{(totalDiscount || 0).toFixed(2)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Net Value</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>₹{(totalNetValue || 0).toFixed(2)}</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Sales Report Generator Component
const SalesReport = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [searchBy, setSearchBy] = useState("datewise");
  const [reportType, setReportType] = useState("summary");
  const [filters, setFilters] = useState({
    store: ["All Stores"],
    customer: ["All Customers"],
    group: ["All Group Names"],
    category: ["All Categories"],
    brand: ["All Brands"],
    product: ["All Products"],
    salesman: ["All Salesmen"]
  });
  const [stores, setStores] = useState(["All Stores"]);
  const [customers, setCustomers] = useState(["All Customers"]);
  const [groups, setGroups] = useState(["All Group Names"]);
  const [categories, setCategories] = useState(["All Categories"]);
  const [brands, setBrands] = useState(["All Brands"]);
  const [products, setProducts] = useState(["All Products"]);
  const [salesmen, setSalesmen] = useState(["All Salesmen"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [salesData, setSalesData] = useState([]);
  const [productGroupMap, setProductGroupMap] = useState({});
  const [productCategoryMap, setProductCategoryMap] = useState({});
  const [productBrandMap, setProductBrandMap] = useState({});

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Get available report types based on search criteria
  const getAvailableReportTypes = () => {
    switch (searchBy) {
      case "datewise":
      case "customer":
      case "category":
      case "brand":
      case "groupwise":
      case "productwise":
        return [{ value: "summary", label: "Abstract" }];
      case "invoicewise":
        return [{ value: "detailed", label: "Details" }];
      case "counterwise":
      case "termwise":
        return [
          { value: "summary", label: "Abstract" },
          { value: "detailed", label: "Details" }
        ];
      default:
        return [
          { value: "summary", label: "Abstract" },
          { value: "detailed", label: "Details" }
        ];
    }
  };

  // Update report type when search criteria changes
  useEffect(() => {
    const availableTypes = getAvailableReportTypes();
    if (!availableTypes.find(type => type.value === reportType)) {
      setReportType(availableTypes[0].value);
    }
  }, [searchBy]);

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  
  // Extract unique salesmen from sales data if salesmen collection is empty
useEffect(() => {
  if (salesData.length > 0 && salesmen.length <= 1) {
    const uniqueSalesmen = ["All Salesmen"];
    salesData.forEach(sale => {
      if (sale.salesMan && !uniqueSalesmen.includes(sale.salesMan)) {
        uniqueSalesmen.push(sale.salesMan);
      }
    });
    setSalesmen(uniqueSalesmen);
  }
}, [salesData]);

// Add after the salesmen loading section to debug
useEffect(() => {
  console.log("Salesmen loaded:", salesmen);
}, [salesmen]);

useEffect(() => {
  console.log("Sales data loaded:", salesData.map(s => s.salesMan));
}, [salesData]);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
      }
    });

    // Load Stores
    const storesRef = ref(database, `users/${encodedEmail}/stores`);
    onValue(storesRef, (snapshot) => {
      const storesData = ["All Stores"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const store = childSnapshot.val();
          if (store.name && !storesData.includes(store.name)) {
            storesData.push(store.name);
          }
        });
      }
      setStores(storesData);
    });

    // Load Customers
    const customersRef = ref(database, `users/${encodedEmail}/customers`);
    onValue(customersRef, (snapshot) => {
      const customersData = ["All Customers"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const customer = childSnapshot.val();
          if (customer.customerName && !customersData.includes(customer.customerName)) {
            customersData.push(customer.customerName);
          }
        });
      }
      setCustomers(customersData);
    });

    // Load Groups
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`);
    onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Group Names"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val();
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name);
          }
        });
      }
      setGroups(groupsData);
    });

    // Load Categories
    const categoriesRef = ref(database, `users/${encodedEmail}/categories`);
    onValue(categoriesRef, (snapshot) => {
      const categoriesData = ["All Categories"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const category = childSnapshot.val();
          if (category.name && !categoriesData.includes(category.name)) {
            categoriesData.push(category.name);
          }
        });
      }
      setCategories(categoriesData);
    });

    // Load Brands
    const brandsRef = ref(database, `users/${encodedEmail}/brands`);
    onValue(brandsRef, (snapshot) => {
      const brandsData = ["All Brands"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const brand = childSnapshot.val();
          if (brand.name && !brandsData.includes(brand.name)) {
            brandsData.push(brand.name);
          }
        });
      }
      setBrands(brandsData);
    });

    // Load Products and their mappings
    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"];
      const groupMap = {};
      const categoryMap = {};
      const brandMap = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val();
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName);
            groupMap[product.productName] = product.groupName || product.group || 'No Group Assigned';
            categoryMap[product.productName] = product.category || 'Unknown Category';
            brandMap[product.productName] = product.brand || 'Unknown Brand';
          }
        });
      }
      setProducts(productsData);
      setProductGroupMap(groupMap);
      setProductCategoryMap(categoryMap);
      setProductBrandMap(brandMap);
    });

 // Load Salesmen
const salesmenRef = ref(database, `users/${encodedEmail}/salesmen`);
onValue(salesmenRef, (snapshot) => {
  const salesmenData = ["All Salesmen"];
  if (snapshot.exists()) {
    snapshot.forEach((childSnapshot) => {
      const salesman = childSnapshot.val();
      // Check both 'name' and 'salesmanName' fields
      const salesmanName = salesman.name || salesman.salesmanName;
      if (salesmanName && !salesmenData.includes(salesmanName)) {
        salesmenData.push(salesmanName);
      }
    });
  }
  setSalesmen(salesmenData);
});

// In the loadFirebaseData function, update the Sales Data loading section:
const salesRef = ref(database, `users/${encodedEmail}/sales`);
onValue(salesRef, (snapshot) => {
  const data = snapshot.val();
  if (data) {
    const salesRecords = [];
    
    Object.entries(data).forEach(([saleId, saleData]) => {
      if (saleData.items && Array.isArray(saleData.items)) {
        saleData.items.forEach(item => {
          if (item.itemDescription) {
            const qty = parseFloat(item.qty) || 0;
            const unitRate = parseFloat(item.unitRate) || 0;
            const discPercent = parseFloat(item.discPercent) || 0;
            const tax = parseFloat(item.tax) || 0;
            const netValue = parseFloat(item.netValue) || 0;
            const grossAmount = qty * unitRate;
            const discount = (grossAmount * discPercent) / 100;
            const gstValue = ((grossAmount - discount) * tax) / 100;
            const taxAmount = gstValue;
            const netAmount = grossAmount - discount + gstValue;

            salesRecords.push({
              saleId: saleId,
              billNo: saleData.billNo || 'N/A',
              date: saleData.date || '',
              store: saleData.store || 'Unknown Store',
              customer: saleData.customer || 'Unknown Customer',
              salesMan: saleData.salesMan || saleData.salesman || 'Unknown Salesman', // ← FIX: Added fallback
              itemDescription: item.itemDescription || 'Unknown Product',
              groupName: productGroupMap[item.itemDescription] || 'No Group Assigned',
              category: productCategoryMap[item.itemDescription] || 'Unknown Category',
              brand: productBrandMap[item.itemDescription] || 'Unknown Brand',
              unit: item.unit || '',
              qty: qty,
              unitRate: unitRate,
              discount: discount,
              gstValue: gstValue,
              taxAmount: gstValue,
              cessAmount: 0,
              billDiscount: 0,
              roundOff: 0,
              netValue: netValue,
              netAmount: netAmount,
              hsnCode: item.hsnCode || 'N/A',
              phone: saleData.phone || 'N/A',
              gstin: saleData.gstin || 'N/A',
              barCode: item.barCode || 'N/A',
              paymentMode: saleData.paymentMode || 'Cash',
              batch: item.batch || 'N/A',
              expiry: item.expiry || 'N/A',
              tax: tax,
              counter: saleData.counter || 'Unknown Counter' // ← FIX: Added counter field
            });
          }
        });
      }
    });
    
    setSalesData(salesRecords);
  } else {
    setSalesData([]);
  }
});

    return () => {
      off(branchProfileRef);
      off(storesRef);
      off(customersRef);
      off(groupsRef);
      off(categoriesRef);
      off(brandsRef);
      off(productsRef);
      off(salesmenRef);
      off(salesRef);
    };
  };

  // Update sales data when productMaps change
  useEffect(() => {
    if (Object.keys(productGroupMap).length > 0 && currentUser) {
      setSalesData(prevData => 
        prevData.map(item => ({
          ...item,
          groupName: productGroupMap[item.itemDescription] || 'No Group Assigned',
          category: productCategoryMap[item.itemDescription] || 'Unknown Category',
          brand: productBrandMap[item.itemDescription] || 'Unknown Brand'
        }))
      );
    }
  }, [productGroupMap, productCategoryMap, productBrandMap, currentUser]);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

 const handlePrint = () => {
  const printContent = (
    <SalesReportDisplay 
      formData={{ fromDate, toDate, searchBy, reportType }}
      filters={filters}
      branchProfile={branchProfile}
      salesData={salesData}
    />
  );

  const htmlString = ReactDOMServer.renderToString(printContent);

  const printWindow = window.open('', '', 'width=1200,height=800');
  printWindow.document.write(`
    <html>
      <head>
        <title>Sales Report</title>
        <style>
          body { 
            margin: 0; 
            padding: 5px; 
            font-family: Arial, sans-serif; 
            font-size: 8px;
          }
          @page { 
            size: A4 landscape; 
            margin: 5mm; 
          }
          table { 
            page-break-inside: auto;
            width: 100%;
            font-size: 7px;
            table-layout: fixed;
            border-collapse: collapse;
          }
          tr { 
            page-break-inside: avoid; 
            page-break-after: auto; 
          }
          th, td {
              padding: 4px !important;
          }
          
          /* Termwise summary columns */
          .termwise-summary th:nth-child(1), .termwise-summary td:nth-child(1) { width: 30px; } /* S.No */
          .termwise-summary th:nth-child(2), .termwise-summary td:nth-child(2) { width: 60px; } /* Date */
          .termwise-summary th:nth-child(3), .termwise-summary td:nth-child(3) { width: 50px; } /* Cash */
          .termwise-summary th:nth-child(4), .termwise-summary td:nth-child(4) { width: 50px; } /* Card */
          .termwise-summary th:nth-child(5), .termwise-summary td:nth-child(5) { width: 50px; } /* Credit */
          .termwise-summary th:nth-child(6), .termwise-summary td:nth-child(6) { width: 60px; } /* Total */

          /* Termwise detailed columns */
          .termwise-detailed th:nth-child(1), .termwise-detailed td:nth-child(1) { width: 30px; } /* S.No */
          .termwise-detailed th:nth-child(2), .termwise-detailed td:nth-child(2) { width: 50px; } /* Invoice */
          .termwise-detailed th:nth-child(3), .termwise-detailed td:nth-child(3) { width: 50px; } /* Date */
          .termwise-detailed th:nth-child(4), .termwise-detailed td:nth-child(4) { width: 80px; } /* Customer */
          .termwise-detailed th:nth-child(5), .termwise-detailed td:nth-child(5) { width: 50px; } /* Cash */
          .termwise-detailed th:nth-child(6), .termwise-detailed td:nth-child(6) { width: 50px; } /* Card */
          .termwise-detailed th:nth-child(7), .termwise-detailed td:nth-child(7) { width: 50px; } /* Credit */
          .termwise-detailed th:nth-child(8), .termwise-detailed td:nth-child(8) { width: 60px; } /* Net Value */

          /* Invoicewise detailed columns */
          .invoicewise-detailed th:nth-child(1), .invoicewise-detailed td:nth-child(1) { width: 25px; } /* S.No */
          .invoicewise-detailed th:nth-child(2), .invoicewise-detailed td:nth-child(2) { width: 40px; } /* Inv No */
          .invoicewise-detailed th:nth-child(3), .invoicewise-detailed td:nth-child(3) { width: 40px; } /* Date */
          .invoicewise-detailed th:nth-child(4), .invoicewise-detailed td:nth-child(4) { width: 35px; } /* HSN */
          .invoicewise-detailed th:nth-child(5), .invoicewise-detailed td:nth-child(5) { width: 45px; } /* Salesman */
          .invoicewise-detailed th:nth-child(6), .invoicewise-detailed td:nth-child(6) { width: 50px; } /* Customer */
          .invoicewise-detailed th:nth-child(7), .invoicewise-detailed td:nth-child(7) { width: 40px; } /* Phone */
    

          .invoicewise-detailed th:nth-child(10), .invoicewise-detailed td:nth-child(10) { width: 35px; } /* Payment */
          .invoicewise-detailed th:nth-child(11), .invoicewise-detailed td:nth-child(11) { width: 70px; } /* Product */

          .invoicewise-detailed th:nth-child(14), .invoicewise-detailed td:nth-child(14) { width: 30px; } /* Qty */
          .invoicewise-detailed th:nth-child(15), .invoicewise-detailed td:nth-child(15) { width: 30px; } /* Rate */
          .invoicewise-detailed th:nth-child(16), .invoicewise-detailed td:nth-child(16) { width: 25px; } /* Tax% */
          .invoicewise-detailed th:nth-child(17), .invoicewise-detailed td:nth-child(17) { width: 35px; } /* Gross */
          .invoicewise-detailed th:nth-child(18), .invoicewise-detailed td:nth-child(18) { width: 30px; } /* Disc */
          .invoicewise-detailed th:nth-child(19), .invoicewise-detailed td:nth-child(19) { width: 30px; } /* CGST */
          .invoicewise-detailed th:nth-child(20), .invoicewise-detailed td:nth-child(20) { width: 30px; } /* SGST */
          .invoicewise-detailed th:nth-child(21), .invoicewise-detailed td:nth-child(21) { width: 30px; } /* IGST */
          .invoicewise-detailed th:nth-child(22), .invoicewise-detailed td:nth-child(22) { width: 35px; } /* GST */
          .invoicewise-detailed th:nth-child(23), .invoicewise-detailed td:nth-child(23) { width: 30px; } /* CESS */
          .invoicewise-detailed th:nth-child(24), .invoicewise-detailed td:nth-child(24) { width: 40px; } /* Net */

          /* Summary report columns */
          .summary th:nth-child(1), .summary td:nth-child(1) { width: 30px; } /* S.No */
          .summary th:nth-child(2), .summary td:nth-child(2) { width: 100px; } /* Key column */
          .summary th:nth-child(3), .summary td:nth-child(3) { width: 40px; } /* Quantity */
          .summary th:nth-child(4), .summary td:nth-child(4) { width: 45px; } /* Disc */
          .summary th:nth-child(5), .summary td:nth-child(5) { width: 50px; } /* GST Value */
          .summary th:nth-child(6), .summary td:nth-child(6) { width: 45px; } /* CESS Value */
          .summary th:nth-child(7), .summary td:nth-child(7) { width: 60px; } /* Gross Amount */
          .summary th:nth-child(8), .summary td:nth-child(8) { width: 60px; } /* Net Value */

          /* Brand detailed columns */
          .brand-detailed th:nth-child(1), .brand-detailed td:nth-child(1) { width: 25px; } /* S.No */
          .brand-detailed th:nth-child(2), .brand-detailed td:nth-child(2) { width: 60px; } /* Brand */
          .brand-detailed th:nth-child(3), .brand-detailed td:nth-child(3) { width: 45px; } /* Bill No */
          .brand-detailed th:nth-child(4), .brand-detailed td:nth-child(4) { width: 40px; } /* Date */
          .brand-detailed th:nth-child(5), .brand-detailed td:nth-child(5) { width: 50px; } /* Store */
          .brand-detailed th:nth-child(6), .brand-detailed td:nth-child(6) { width: 60px; } /* Customer */
          .brand-detailed th:nth-child(7), .brand-detailed td:nth-child(7) { width: 80px; } /* Product */
          .brand-detailed th:nth-child(8), .brand-detailed td:nth-child(8) { width: 30px; } /* Unit */
          .brand-detailed th:nth-child(9), .brand-detailed td:nth-child(9) { width: 35px; } /* Qty */
          .brand-detailed th:nth-child(10), .brand-detailed td:nth-child(10) { width: 40px; } /* Rate */
          .brand-detailed th:nth-child(11), .brand-detailed td:nth-child(11) { width: 40px; } /* Disc */
          .brand-detailed th:nth-child(12), .brand-detailed td:nth-child(12) { width: 40px; } /* GST */
          .brand-detailed th:nth-child(13), .brand-detailed td:nth-child(13) { width: 40px; } /* CESS */
          .brand-detailed th:nth-child(14), .brand-detailed td:nth-child(14) { width: 40px; } /* Bill Disc */
          .brand-detailed th:nth-child(15), .brand-detailed td:nth-child(15) { width: 35px; } /* Round */
          .brand-detailed th:nth-child(16), .brand-detailed td:nth-child(16) { width: 50px; } /* Gross */
          .brand-detailed th:nth-child(17), .brand-detailed td:nth-child(17) { width: 50px; } /* Net */

          /* Productwise detailed columns */
          .productwise-detailed th:nth-child(1), .productwise-detailed td:nth-child(1) { width: 30px; } /* S.No */
          .productwise-detailed th:nth-child(2), .productwise-detailed td:nth-child(2) { width: 120px; } /* Product */
          .productwise-detailed th:nth-child(3), .productwise-detailed td:nth-child(3) { width: 35px; } /* Unit */
          .productwise-detailed th:nth-child(4), .productwise-detailed td:nth-child(4) { width: 40px; } /* Qty */
          .productwise-detailed th:nth-child(5), .productwise-detailed td:nth-child(5) { width: 45px; } /* Rate */
          .productwise-detailed th:nth-child(6), .productwise-detailed td:nth-child(6) { width: 45px; } /* Disc */
          .productwise-detailed th:nth-child(7), .productwise-detailed td:nth-child(7) { width: 50px; } /* GST */
          .productwise-detailed th:nth-child(8), .productwise-detailed td:nth-child(8) { width: 50px; } /* Gross */
          .productwise-detailed th:nth-child(9), .productwise-detailed td:nth-child(9) { width: 60px; } /* Net */

          /* Default detailed columns */
          .default-detailed th:nth-child(1), .default-detailed td:nth-child(1) { width: 25px; } /* S.No */
          .default-detailed th:nth-child(2), .default-detailed td:nth-child(2) { width: 45px; } /* Bill No */
          .default-detailed th:nth-child(3), .default-detailed td:nth-child(3) { width: 40px; } /* Date */
          .default-detailed th:nth-child(4), .default-detailed td:nth-child(4) { width: 50px; } /* Store */
          .default-detailed th:nth-child(5), .default-detailed td:nth-child(5) { width: 60px; } /* Customer */
          .default-detailed th:nth-child(6), .default-detailed td:nth-child(6) { width: 80px; } /* Product */
          .default-detailed th:nth-child(7), .default-detailed td:nth-child(7) { width: 30px; } /* Unit */
          .default-detailed th:nth-child(8), .default-detailed td:nth-child(8) { width: 35px; } /* Qty */
          .default-detailed th:nth-child(9), .default-detailed td:nth-child(9) { width: 40px; } /* Rate */
          .default-detailed th:nth-child(10), .default-detailed td:nth-child(10) { width: 40px; } /* Disc */
          .default-detailed th:nth-child(11), .default-detailed td:nth-child(11) { width: 40px; } /* GST */
          .default-detailed th:nth-child(12), .default-detailed td:nth-child(12) { width: 35px; } /* CESS */
          .default-detailed th:nth-child(13), .default-detailed td:nth-child(13) { width: 40px; } /* Bill Disc */
          .default-detailed th:nth-child(14), .default-detailed td:nth-child(14) { width: 35px; } /* Round */
          .default-detailed th:nth-child(15), .default-detailed td:nth-child(15) { width: 50px; } /* Gross */
          .default-detailed th:nth-child(16), .default-detailed td:nth-child(16) { width: 50px; } /* Net */
          
          .print-only {
            width: 93% !important;
            max-width: none !important;
            overflow: visible !important;
          }
          div[style*="overflowX"] {
            overflow: visible !important;
          }
        </style>
      </head>
      <body>
        <div id="print-content">${htmlString}</div>
        <script>
          setTimeout(function() {
            window.print();
            window.close();
          }, 1000);
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Sales Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: '#64748b', 
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Sales Report </p>
      </div>
    );
  }

  const availableReportTypes = getAvailableReportTypes();

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Sales Report </h1>
      </div>
      
      {/* Date Range and Search Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Search By</label>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
          <option value="datewise">Datewise</option>
            <option value="customer">Customer</option>
            <option value="groupwise">Groupwise</option>
            <option value="brand">Brand</option>
            <option value="category">Category</option>
            <option value="productwise">Productwise</option>
            <option value="salesman">Salesman</option>
            {/* <option value="counterwise">Counterwise</option> */}
          
          </select>
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            {availableReportTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(195px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
        whiteSpace: 'nowrap'
      }}>
         {/* <FilterSection 
          title="Stores" 
          options={stores} 
          selected={filters.store} 
          onChange={(value) => handleFilterChange("store", value)}
        />  */}
          <FilterSection 
          title="Salesman" 
          options={salesmen} 
          selected={filters.salesman} 
          onChange={(value) => handleFilterChange("salesman", value)}
        />
        <FilterSection 
          title="Customer" 
          options={customers} 
          selected={filters.customer} 
          onChange={(value) => handleFilterChange("customer", value)}
        />
        <FilterSection 
          title="Group Name" 
          options={groups} 
          selected={filters.group} 
          onChange={(value) => handleFilterChange("group", value)}
        />
        <FilterSection 
          title="Category" 
          options={categories} 
          selected={filters.category} 
          onChange={(value) => handleFilterChange("category", value)}
        />
        <FilterSection 
          title="Brand" 
          options={brands} 
          selected={filters.brand} 
          onChange={(value) => handleFilterChange("brand", value)}
        />
        <FilterSection 
          title="Product" 
          options={products} 
          selected={filters.product} 
          onChange={(value) => handleFilterChange("product", value)}
        />
      
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>

      {/* Sales Report Display */}
      {showReport && (
        <SalesReportDisplay 
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          salesData={salesData}
        />
      )}
    </div>
  );
};

export default SalesReport;

